// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a phone's controls in handheld AR (the AR refresh, 2026-10-04).
 *
 * Chrome on Android draws one DOM subtree over the camera picture (the
 * WebXR DOM overlay, `#xr-dom-overlay`). The first AR version used it
 * only for Re-place, the zoom slider and the marker scan; play, Browse
 * and Exit sat on a 7 cm bar in the 3D scene, a metre and a half away and
 * often off the screen. Everything a phone person presses now lives
 * here, at thumb reach:
 *
 *   - a top line: Leave AR, the dataset and its date, Help;
 *   - the dataset's legend, as the browser draws it (a tap opens View);
 *     AYNI (owner, 2026-10-11): its ✕ parks it in the View sheet, where
 *     the browser's parked legend (Tools → View, legendDock.ts) brings it
 *     back;
 *   - a transport: frame back, play/pause, frame forward, a track
 *     through the dataset's dates (or its playing time) and the speed;
 *     AYNI: above it, a line per real-time overlay playing, with its
 *     eye (hide it on the globe) and its ✕ (take it off), as the 2D date
 *     tracks carry them;
 *   - a dock: Browse, Layers, Data, View, Place;
 *   - AYNI (Khipu plan, slice 4): the Khipu panel or the lesson
 *     panel, when one is open, moved into the overlay for the session
 *     (`adoptPanels`) and back to the page after it.
 *
 * Browse is the browser's own panel, lent to the overlay for the session
 * (`#browse-overlay`, already a bottom sheet at phone width), so search,
 * tabs, filters and Khipus come with it. Layers, Data and View are the
 * browser's Tools tab contents, lent to a sheet here the same way. On
 * dispose every lent element goes back where it came from.
 *
 * Touch stays as the owner set it (2026-09-17, 2026-10-02): one finger on
 * the globe spins and tilts, a tap in pin mode drops a pin, no two-finger
 * gesture, and size only through the slider. Every element here carries
 * `xr-phone-ui`, which the rotate layer skips, and stops `beforexrselect`
 * so a press on a button is not also a tap on the globe.
 */

import { t, type MessageKey } from '../i18n'
import { nextSpeed } from '../services/vrHud'
import { escapeHtml } from './domUtils'
import { rampGradient, type RtPlayingEntry } from '../services/overlayDisplay'
import type { ColorScaleDisplay } from '../services/colorScaleDisplay'
import type { ColorScale } from '../types/color-scale'
import { attachStackDrag } from './overlayStackDrag'
import { closePaletteDialog, paletteButtonLabel, togglePaletteDialog } from './overlayPaletteDialog'
import { flyGhost, legendGhost } from './legendDock'

export type XrPhoneSheet = 'browse' | 'layers' | 'data' | 'view' | 'help'

/** What the chrome reads from the session and the app, and what it asks of them. */
export interface XrPhoneChromeHost {
  title(): string | null
  dateLabel(): string | null
  isLive(): boolean
  hasVideo(): boolean
  isPlaying(): boolean
  isMuted(): boolean
  togglePlay(): void
  step(direction: -1 | 1): void
  toggleMute(): void
  speed(): number
  setSpeed(speed: number): void
  /** Where playback is, 0..1, or null when nothing plays. */
  position(): number | null
  /** The track's two end labels (dates, or playing time). */
  trackLabels(): [string, string] | null
  /** Move playback to a fraction of the track. */
  seek(fraction: number): void
  /** A meeting's presenter drives: the transport and the panels are hidden. */
  locked(): boolean
  /**
   * AYNI (Quest review E6, 2026-11-03): the globe follows a room's lead,
   * so touch does not turn it and the slider stands down; a line says so.
   */
  following?(): boolean
  /** A change key for the legend; the element is asked for only when it changes. */
  legendKey(): string
  /** A fresh legend element for the dataset on the globe, or null (none, or parked). */
  legend(): HTMLElement | null
  /** AYNI: its ✕: park it in View (the browser's Tools → View). */
  parkLegend?(): void
  /** Placement is running: the chrome steps aside for its buttons. */
  isPlacing(): boolean
  place(): void
  exit(): void
  /** Make sure the browser's Browse panel is built and open. */
  openBrowse(): void
  /** Collapse it again. */
  closeBrowse(): void
  /** AYNI: the real-time overlays playing, bottom first, whether each is hidden, and its colours. */
  overlays(): ReadonlyArray<RtPlayingEntry>
  setOverlayHidden(id: string, hidden: boolean): void
  closeOverlay(id: string): void
  /** AYNI (2026-10-09): move one to `to` in the stack (0 is the lowest). */
  moveOverlay(id: string, to: number): void
  /** Its colours for the palette dialog; the scale is null when they cannot change. */
  overlayColours(id: string): { scale: ColorScale | null; display: ColorScaleDisplay } | null
  setOverlayDisplay(id: string, display: ColorScaleDisplay): void
  /** AYNI (owner, 2026-10-12): an overlay's opacity, from its dialog's transparency. */
  setOverlayOpacity?(id: string, opacity: number): void
  /** AYNI (owner, 2026-10-12): the dataset under the overlays (its line has a lock, its eye and its ✕). */
  base?(): {
    title: string
    hidden: boolean
    opacity?: number
    /** Its colours for the palette dialog (owner, 2026-10-13); a null scale: they cannot change, `coloursReason` says why. */
    colours?: { scale: ColorScale | null; display: ColorScaleDisplay } | null
    coloursReason?: string
  } | null
  setBaseDisplay?(display: ColorScaleDisplay): void
  setBaseOpacity?(opacity: number): void
  setBaseHidden?(hidden: boolean): void
  closeBase?(): void
}

export interface XrPhoneChromeHandle {
  mount(root: HTMLElement): void
  /** Re-read the host; call a few times a second. */
  update(): void
  /** A short message at the bottom of the screen for a few seconds. */
  notice(text: string): void
  /** A sheet (or Browse) covers the screen: the globe's touch should stand down. */
  sheetOpen(): boolean
  /** Open a sheet, or close them with null. */
  openSheet(sheet: XrPhoneSheet | null): void
  dispose(): void
}

/** The page's panels a phone's AR session shows over the camera while they are open (`adoptPanels`). */
const ADOPTED_PANELS = ['khipu-player', 'lesson-player', 'room-note', 'game-panel'] as const

/** The browser panels lent to the overlay, by id; the first of each is the sheet's own. */
const TOOLS_HOSTS: Record<'layers' | 'data' | 'view', readonly string[]> = {
  layers: ['tools-menu-layers', 'tools-menu-satellites'],
  data: ['tools-data-pins'],
  // AYNI: and the parked legend (legendDock.ts), which hides itself while there is none.
  view: ['tools-view-palette', 'tools-view-opacity', 'tools-view-legend'],
}

const NOTICE_MS = 4000
/** The "one finger" hint stays this long after the session starts. */
const HINT_MS = 7000
/** Seeks while the track is dragged are rationed, as on the 2D track. */
const SEEK_INTERVAL_MS = 250
/** AYNI: the dataset's line among the overlay lines (its buttons' data-overlay). */
const BASE_ROW = '\u0000base'

const ICONS = {
  close: '<path d="M6 6l12 12M18 6L6 18"/>',
  lockSmall: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  eye: '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  eyeOff: '<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/><path d="M3 3l18 18"/>',
  grip: '<g fill="currentColor" stroke="none"><circle cx="9" cy="6" r="1.7"/><circle cx="15" cy="6" r="1.7"/><circle cx="9" cy="12" r="1.7"/><circle cx="15" cy="12" r="1.7"/><circle cx="9" cy="18" r="1.7"/><circle cx="15" cy="18" r="1.7"/></g>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14"/><path d="M12 17.5h.01"/>',
  play: '<path d="M8 5.5v13l11-6.5z" fill="currentColor" stroke="none"/>',
  pause: '<path d="M9 6v12M15 6v12" stroke-width="2.6"/>',
  back: '<path d="M18 6l-8 6 8 6zM6 6v12"/>',
  forward: '<path d="M6 6l8 6-8 6zM18 6v12"/>',
  sound: '<path d="M4 10v4h4l5 4V6L8 10z"/><path d="M16 9a4 4 0 0 1 0 6"/>',
  muted: '<path d="M4 10v4h4l5 4V6L8 10z"/><path d="M16 9l5 6M21 9l-5 6"/>',
  browse: '<rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/>',
  layers: '<path d="M12 3l9 5-9 5-9-5z"/><path d="M3 13l9 5 9-5"/>',
  data: '<path d="M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11z"/><circle cx="12" cy="10" r="2.2"/>',
  view: '<path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/>',
  place: '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="2"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>',
} as const

function svg(name: keyof typeof ICONS): string {
  return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICONS[name]}</svg>`
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, html = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  node.className = className
  if (html) node.innerHTML = html
  return node
}

function iconButton(className: string, icon: keyof typeof ICONS, label: string): HTMLButtonElement {
  const b = el('button', className, svg(icon))
  b.type = 'button'
  b.setAttribute('aria-label', label)
  b.title = label
  return b
}

interface Lent {
  node: HTMLElement
  parent: Node
  next: Node | null
  hadHidden: boolean
}

const DOCK: ReadonlyArray<{ id: XrPhoneSheet | 'place'; icon: keyof typeof ICONS; label: MessageKey }> = [
  { id: 'browse', icon: 'browse', label: 'vr.dock.browse' },
  { id: 'layers', icon: 'layers', label: 'vr.dock.layers' },
  { id: 'data', icon: 'data', label: 'vr.dock.data' },
  { id: 'view', icon: 'view', label: 'vr.dock.view' },
  { id: 'place', icon: 'place', label: 'vr.phone.place' },
]

export function createXrPhoneChrome(host: XrPhoneChromeHost): XrPhoneChromeHandle {
  let root: HTMLElement | null = null
  let sheet: XrPhoneSheet | null = null
  let legendKey = '\u0000'
  let noticeTimer: number | null = null
  let lastSeekAt = 0
  let dragging = false
  const startedAt = Date.now()
  const lent = new Map<string, Lent>()

  const stopXr = (ev: Event): void => ev.preventDefault()
  /** The page's Khipu and lesson panels moved into the overlay, while they stay open. */
  const adopted = new Set<HTMLElement>()

  /** Move an open Khipu or lesson panel into the overlay, so it shows over the camera. */
  function adoptPanels(): void {
    if (!root) return
    for (const id of ADOPTED_PANELS) {
      const node = document.getElementById(id)
      if (!node || node.parentNode === root) continue
      node.classList.add('xr-phone-ui', 'xr-phone-adopted')
      node.addEventListener('beforexrselect', stopXr)
      root.appendChild(node)
      adopted.add(node)
    }
    for (const node of adopted) if (!node.isConnected) adopted.delete(node)
    // The panel stands under the legend chip, whose height follows its dataset.
    const legend = adopted.size ? root.querySelector<HTMLElement>('.xr-phone-legend') : null
    const rect = legend && legend.getClientRects().length ? legend.getBoundingClientRect() : null
    if (rect && rect.height > 0) root.style.setProperty('--xr-phone-legend-bottom', `${Math.round(rect.bottom)}px`)
    else root.style.removeProperty('--xr-phone-legend-bottom')
  }

  /** Give the panels still open back to the page. */
  function returnPanels(): void {
    for (const node of adopted) {
      node.removeEventListener('beforexrselect', stopXr)
      node.classList.remove('xr-phone-ui', 'xr-phone-adopted')
      if (node.isConnected) document.body.appendChild(node)
    }
    adopted.clear()
    root?.style.removeProperty('--xr-phone-legend-bottom')
  }

  // --- Top line.
  const top = el('div', 'xr-phone-top xr-phone-ui')
  const exitBtn = iconButton('xr-phone-round', 'close', t('vr.phone.exit'))
  const titleBox = el('div', 'xr-phone-title')
  const titleText = el('span', 'xr-phone-title-name')
  const dateText = el('span', 'xr-phone-title-date')
  titleBox.append(titleText, dateText)
  const helpBtn = iconButton('xr-phone-round', 'help', t('vr.phone.help'))
  top.append(exitBtn, titleBox, helpBtn)

  // --- Legend (a tap opens View).
  const legendBtn = el('button', 'xr-phone-legend xr-phone-ui hidden')
  legendBtn.type = 'button'
  legendBtn.setAttribute('aria-label', t('vr.phone.legend'))
  // A span, inside the chip's button.
  const legendClose = el('span', 'legend-close xr-phone-legend-close')
  legendClose.setAttribute('role', 'button')
  legendClose.tabIndex = 0
  legendClose.setAttribute('aria-label', t('legend.close'))
  legendClose.textContent = '✕'
  legendClose.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return
    ev.preventDefault()
    legendClose.click()
  })

  // --- Locked line (a meeting's presenter drives).
  const lockedLine = el('div', 'xr-phone-locked xr-phone-ui hidden')
  lockedLine.setAttribute('role', 'status')
  lockedLine.textContent = t('vr.dock.locked')

  // --- First-use hint.
  const hint = el('div', 'xr-phone-hint xr-phone-ui')
  hint.setAttribute('role', 'status')
  hint.textContent = t('vr.phone.hint')

  // --- Transport.
  const transport = el('div', 'xr-phone-transport xr-phone-ui hidden')
  transport.setAttribute('role', 'toolbar')
  transport.setAttribute('aria-label', t('playback.toolbar.aria'))
  const backBtn = iconButton('xr-phone-tbtn', 'back', t('playback.stepBack.aria'))
  const playBtn = iconButton('xr-phone-tbtn xr-phone-play', 'play', t('playback.play.aria'))
  const fwdBtn = iconButton('xr-phone-tbtn', 'forward', t('playback.stepForward.aria'))
  const muteBtn = iconButton('xr-phone-tbtn', 'muted', t('playback.unmute.aria'))
  const trackBox = el('div', 'xr-phone-track')
  const track = el('input', 'xr-phone-track-input')
  track.type = 'range'
  track.min = '0'
  track.max = '1000'
  track.value = '0'
  track.setAttribute('aria-label', t('playback.scrubber.aria'))
  const trackLabels = el('div', 'xr-phone-track-labels')
  const trackStart = el('span', '')
  const trackEnd = el('span', '')
  trackLabels.append(trackStart, trackEnd)
  trackBox.append(track, trackLabels)
  const speedBtn = el('button', 'xr-phone-tbtn xr-phone-speed')
  speedBtn.type = 'button'
  speedBtn.setAttribute('aria-label', t('vr.phone.speed'))
  // AYNI: the overlays' lines, first in the box, which wraps them above the controls.
  const overlayRows = el('div', 'xr-phone-overlays hidden')
  let overlaysKey = ''
  /** AYNI: the rows' drags, unwired when the rows are rebuilt. */
  let rowDrags: Array<() => void> = []
  /** AYNI: the overlays' ids as the rows list them, top first. */
  let rowIds: string[] = []
  transport.append(overlayRows, backBtn, playBtn, fwdBtn, trackBox, speedBtn, muteBtn)

  // --- Dock.
  const dock = el('nav', 'xr-phone-dock xr-phone-ui')
  dock.setAttribute('aria-label', t('vr.phone.dock'))
  const dockButtons = new Map<string, HTMLButtonElement>()
  for (const d of DOCK) {
    const b = el('button', 'xr-phone-dock-btn', `${svg(d.icon)}<span>${escapeHtml(t(d.label))}</span>`)
    b.type = 'button'
    b.dataset.sheet = d.id
    dockButtons.set(d.id, b)
    dock.appendChild(b)
  }

  // --- Tools sheet (Layers, Data, View) and the Help sheet.
  const toolsSheet = el('section', 'xr-phone-sheet xr-phone-ui hidden')
  toolsSheet.setAttribute('role', 'dialog')
  toolsSheet.setAttribute('aria-label', t('vr.phone.tools'))
  const grabber = el('div', 'xr-phone-sheet-grabber')
  const sheetHead = el('div', 'xr-phone-sheet-head')
  const tabs = el('div', 'xr-phone-tabs')
  tabs.setAttribute('role', 'tablist')
  const tabButtons = new Map<'layers' | 'data' | 'view', HTMLButtonElement>()
  for (const id of ['layers', 'data', 'view'] as const) {
    const b = el('button', 'xr-phone-tab')
    b.type = 'button'
    b.setAttribute('role', 'tab')
    b.textContent = t(id === 'layers' ? 'vr.dock.layers' : id === 'data' ? 'vr.dock.data' : 'vr.dock.view')
    b.dataset.sheet = id
    tabButtons.set(id, b)
    tabs.appendChild(b)
  }
  const sheetClose = iconButton('xr-phone-round xr-phone-sheet-close', 'close', t('vr.phone.closeSheet'))
  sheetHead.append(tabs, sheetClose)
  const sheetBody = el('div', 'xr-phone-sheet-body')
  const sheetEmpty = el('p', 'xr-phone-sheet-empty hidden')
  sheetBody.appendChild(sheetEmpty)
  toolsSheet.append(grabber, sheetHead, sheetBody)

  const helpSheet = el('section', 'xr-phone-sheet xr-phone-help xr-phone-ui hidden')
  helpSheet.setAttribute('role', 'dialog')
  helpSheet.setAttribute('aria-label', t('vr.phone.help'))
  const helpHead = el('div', 'xr-phone-sheet-head')
  const helpTitle = el('h2', 'xr-phone-help-title')
  helpTitle.textContent = t('vr.phone.helpTitle')
  const helpClose = iconButton('xr-phone-round xr-phone-sheet-close', 'close', t('vr.phone.closeSheet'))
  helpHead.append(helpTitle, helpClose)
  const helpList = el('ul', 'xr-phone-help-list')
  for (const key of ['vr.phone.tip.spin', 'vr.phone.tip.size', 'vr.phone.tip.place', 'vr.phone.tip.pins', 'vr.phone.tip.browse', 'vr.phone.tip.exit'] as const) {
    const li = el('li', '')
    li.textContent = t(key)
    helpList.appendChild(li)
  }
  const helpExit = el('button', 'xr-phone-help-exit')
  helpExit.type = 'button'
  helpExit.textContent = t('vr.phone.exit')
  helpSheet.append(el('div', 'xr-phone-sheet-grabber'), helpHead, helpList, helpExit)

  // --- Notice.
  const toast = el('div', 'xr-phone-toast xr-phone-ui hidden')
  toast.setAttribute('role', 'status')
  toast.setAttribute('aria-live', 'polite')

  const parts: HTMLElement[] = [top, legendBtn, lockedLine, hint, transport, dock, toolsSheet, helpSheet, toast]

  // --- Lending the browser's panels.
  function lend(id: string, into: HTMLElement): HTMLElement | null {
    const existing = lent.get(id)
    if (existing) {
      if (existing.node.parentNode !== into) into.appendChild(existing.node)
      return existing.node
    }
    const node = document.getElementById(id)
    if (!node || !node.parentNode) return null
    lent.set(id, { node, parent: node.parentNode, next: node.nextSibling, hadHidden: node.hasAttribute('hidden') })
    node.addEventListener('beforexrselect', stopXr)
    into.appendChild(node)
    return node
  }

  function giveBackAll(): void {
    for (const [, l] of lent) {
      l.node.removeEventListener('beforexrselect', stopXr)
      l.node.classList.remove('xr-phone-lent')
      l.node.style.display = ''
      // The Layers section hides itself while it has nothing to offer.
      if (l.hadHidden && l.node.childElementCount === 0) l.node.setAttribute('hidden', '')
      if (l.next && l.next.parentNode === l.parent) l.parent.insertBefore(l.node, l.next)
      else l.parent.appendChild(l.node)
    }
    lent.clear()
  }

  function browseIsOpen(): boolean {
    const overlay = lent.get('browse-overlay')?.node
    return !!overlay && overlay.parentNode === root && !overlay.classList.contains('collapsed') && !overlay.classList.contains('hidden')
  }

  function syncSheetClasses(): void {
    const open = sheet !== null
    root?.classList.toggle('xr-sheet-open', open)
    toolsSheet.classList.toggle('hidden', !(sheet === 'layers' || sheet === 'data' || sheet === 'view'))
    helpSheet.classList.toggle('hidden', sheet !== 'help')
    for (const [id, b] of dockButtons) b.classList.toggle('active', id === sheet)
    for (const [id, b] of tabButtons) {
      const on = id === sheet
      b.classList.toggle('active', on)
      b.setAttribute('aria-selected', String(on))
    }
  }

  function showToolsHost(which: 'layers' | 'data' | 'view'): void {
    for (const key of Object.keys(TOOLS_HOSTS) as Array<'layers' | 'data' | 'view'>) {
      TOOLS_HOSTS[key].forEach((id, i) => {
        const node = lend(id, sheetBody)
        if (!node) return
        node.classList.add('xr-phone-lent')
        node.style.display = key === which ? '' : 'none'
        // The Layers section hides itself while there is nothing to choose.
        if (key === which && i === 0) node.removeAttribute('hidden')
      })
    }
    const node = lent.get(TOOLS_HOSTS[which][0])?.node
    const empty = !node || (which === 'layers' && node.childElementCount === 0)
    sheetEmpty.textContent = t('vr.phone.noLayers')
    sheetEmpty.classList.toggle('hidden', !empty)
  }

  function openSheet(next: XrPhoneSheet | null): void {
    if (next === sheet && next !== null) next = null
    if (sheet === 'browse' && next !== 'browse') host.closeBrowse()
    sheet = next
    if (next === 'browse' && root) {
      host.openBrowse()
      const overlay = lend('browse-overlay', root)
      overlay?.classList.add('xr-phone-lent')
    } else if (next === 'layers' || next === 'data' || next === 'view') {
      showToolsHost(next)
    }
    syncSheetClasses()
  }

  function notice(text: string): void {
    toast.textContent = text
    toast.classList.remove('hidden')
    if (noticeTimer !== null) window.clearTimeout(noticeTimer)
    noticeTimer = window.setTimeout(() => {
      toast.classList.add('hidden')
      noticeTimer = null
    }, NOTICE_MS)
  }

  // --- Wiring.
  exitBtn.addEventListener('click', () => host.exit())
  helpExit.addEventListener('click', () => host.exit())
  helpBtn.addEventListener('click', () => openSheet('help'))
  helpClose.addEventListener('click', () => openSheet(null))
  sheetClose.addEventListener('click', () => openSheet(null))
  legendBtn.addEventListener('click', () => openSheet('view'))
  // AYNI: the ✕ parks it; it flies into the dock's View button on the way.
  legendClose.addEventListener('click', (ev) => {
    ev.stopPropagation()
    ev.preventDefault()
    const target = dockButtons.get('view')
    if (root && target && legendBtn.getClientRects().length) {
      const from = legendBtn.getBoundingClientRect()
      void flyGhost(legendGhost(legendBtn, from, root), from, target.getBoundingClientRect(), { fadeOut: true })
    }
    host.parkLegend?.()
    update()
  })
  for (const [id, b] of dockButtons) {
    b.addEventListener('click', () => {
      if (id === 'place') {
        openSheet(null)
        host.place()
        return
      }
      openSheet(id as XrPhoneSheet)
    })
  }
  for (const [id, b] of tabButtons) b.addEventListener('click', () => openSheet(id))
  playBtn.addEventListener('click', () => host.togglePlay())
  backBtn.addEventListener('click', () => host.step(-1))
  fwdBtn.addEventListener('click', () => host.step(1))
  muteBtn.addEventListener('click', () => host.toggleMute())
  speedBtn.addEventListener('click', () => host.setSpeed(nextSpeed(host.speed())))
  overlayRows.addEventListener('click', (ev) => {
    const b = (ev.target as Element | null)?.closest<HTMLButtonElement>('button[data-overlay]')
    if (!b) return
    const id = b.dataset.overlay!
    // AYNI: the grip only drags; the palette button opens the colour dialog over the camera.
    if (b.dataset.action === 'grip') return
    if (b.dataset.action === 'palette') {
      if (id === BASE_ROW) openBasePalette(b)
      else openOverlayPalette(id, b)
      return
    }
    // AYNI (owner, 2026-10-12): the dataset's line.
    if (id === BASE_ROW) {
      if (b.dataset.action === 'close') host.closeBase?.()
      else host.setBaseHidden?.(b.getAttribute('aria-pressed') !== 'true')
      update()
      return
    }
    if (b.dataset.action === 'close') host.closeOverlay(id)
    else host.setOverlayHidden(id, b.getAttribute('aria-pressed') !== 'true')
    update()
  })
  track.addEventListener('pointerdown', () => { dragging = true })
  track.addEventListener('input', () => {
    const now = Date.now()
    if (now - lastSeekAt < SEEK_INTERVAL_MS) return
    lastSeekAt = now
    host.seek(Number(track.value) / 1000)
  })
  track.addEventListener('change', () => {
    dragging = false
    host.seek(Number(track.value) / 1000)
  })

  function update(): void {
    if (!root) return
    adoptPanels()
    const placing = host.isPlacing()
    const locked = host.locked()
    root.classList.toggle('xr-phone-placing', placing)

    // Browse closes itself when a dataset is chosen.
    if (sheet === 'browse' && !browseIsOpen()) {
      sheet = null
      syncSheetClasses()
    }

    titleText.textContent = host.title() ?? t('vr.dock.noDataset')
    const date = host.dateLabel()
    dateText.textContent = ''
    if (host.isLive()) {
      const live = el('span', 'xr-phone-live')
      live.textContent = t('vr.phone.live')
      dateText.appendChild(live)
      if (date) dateText.append(` · ${date}`)
    } else if (date) {
      dateText.textContent = date
    }

    const key = host.legendKey()
    if (key !== legendKey) {
      legendKey = key
      const wasHidden = legendBtn.classList.contains('hidden')
      legendBtn.textContent = ''
      const legend = host.legend()
      if (legend) {
        legendBtn.appendChild(legend)
        if (host.parkLegend) legendBtn.appendChild(legendClose)
      }
      legendBtn.classList.toggle('hidden', !legend)
      // Back from the View sheet: it grows into its place.
      if (legend && wasHidden) {
        legendBtn.classList.remove('xr-phone-legend-in')
        void legendBtn.offsetWidth
        legendBtn.classList.add('xr-phone-legend-in')
      }
    }

    const following = !locked && (host.following?.() ?? false)
    hint.classList.toggle('hidden', Date.now() - startedAt > HINT_MS || placing || sheet !== null || following)
    // AYNI (Quest review E6): the same line says when the globe is following.
    const lineKey = locked ? 'vr.dock.locked' : 'vr.phone.following'
    if (lockedLine.dataset.key !== lineKey) {
      lockedLine.dataset.key = lineKey
      lockedLine.textContent = t(lineKey)
    }
    lockedLine.classList.toggle('hidden', !locked && !following)
    // The legend and the scan button step down a row under it (vr.css).
    root.classList.toggle('xr-phone-has-line', locked || following)

    const video = host.hasVideo() && !locked
    transport.classList.toggle('hidden', !video)
    if (video) {
      const playing = host.isPlaying()
      playBtn.innerHTML = svg(playing ? 'pause' : 'play')
      playBtn.setAttribute('aria-label', t(playing ? 'playback.pause.aria' : 'playback.play.aria'))
      const muted = host.isMuted()
      muteBtn.innerHTML = svg(muted ? 'muted' : 'sound')
      muteBtn.setAttribute('aria-label', t(muted ? 'playback.unmute.aria' : 'vr.phone.mute'))
      speedBtn.textContent = t('vr.dock.speed', { speed: formatSpeed(host.speed()) })
      const pos = host.position()
      trackBox.classList.toggle('hidden', pos === null)
      if (pos !== null && !dragging) track.value = String(Math.round(pos * 1000))
      const labels = host.trackLabels()
      trackStart.textContent = labels?.[0] ?? ''
      trackEnd.textContent = labels?.[1] ?? ''
    }
    syncOverlays(video)
    for (const id of ['browse', 'layers', 'data', 'view'] as const) {
      dockButtons.get(id)!.classList.toggle('hidden', locked)
    }
    if (locked && sheet !== null && sheet !== 'help') openSheet(null)
  }

  /** AYNI: an overlay's palette and min/max dialog, in the overlay so it shows over the camera. */
  function openOverlayPalette(id: string, button: HTMLButtonElement): void {
    const entry = host.overlays().find((o) => o.id === id)
    const colours = host.overlayColours(id)
    if (!entry || !colours) return
    // AYNI (owner, 2026-10-12): every overlay opens, for its transparency at least.
    const dialog = togglePaletteDialog({
      anchor: button,
      container: root ?? undefined,
      beside: transport,
      title: entry.title,
      accent: 'overlay',
      scale: colours.scale,
      fixedReason: entry.coloursReason,
      display: colours.display,
      onChange: (next) => host.setOverlayDisplay(id, next),
      opacity: entry.opacity ?? 1,
      ...(host.setOverlayOpacity ? { onOpacityChange: (opacity: number) => host.setOverlayOpacity?.(id, opacity) } : {}),
    })
    // A tap on it is for it, not a select in the AR session (placing, a pin).
    dialog?.element.addEventListener('beforexrselect', stopXr)
  }

  /** AYNI (owner, 2026-10-13): the dataset's palette and transparency, the same dialog in its green. */
  function openBasePalette(button: HTMLButtonElement): void {
    const base = host.base?.() ?? null
    if (!base?.colours) return
    const dialog = togglePaletteDialog({
      anchor: button,
      container: root ?? undefined,
      beside: transport,
      title: base.title,
      accent: 'base',
      scale: base.colours.scale,
      fixedReason: base.coloursReason,
      display: base.colours.display,
      onChange: (next) => host.setBaseDisplay?.(next),
      opacity: base.opacity ?? 1,
      ...(host.setBaseOpacity ? { onOpacityChange: (opacity: number) => host.setBaseOpacity?.(opacity) } : {}),
    })
    dialog?.element.addEventListener('beforexrselect', stopXr)
  }

  /** The ramp a palette button shows for colours, or '' (hatched) when they cannot change. */
  function rampOf(colours: { scale: ColorScale | null; display: ColorScaleDisplay } | null | undefined): string {
    return colours?.scale ? rampGradient(colours.scale, colours.display, 'to top') : ''
  }

  /** AYNI: one line per playing overlay, top first (the top one is drawn on top); rebuilt only when one changes. */
  function syncOverlays(shown: boolean): void {
    const list = shown ? [...host.overlays()].reverse() : []
    // The palette buttons' ramps change in place, so an open dialog keeps its button.
    for (const o of list) {
      const ramp = overlayRows.querySelector<HTMLElement>(`button[data-action="palette"][data-overlay="${CSS.escape(o.id)}"] .xr-phone-overlay-ramp`)
      if (ramp) ramp.style.background = o.colours ? `linear-gradient(to top, ${o.colours.ramp.join(', ')})` : ''
    }
    // AYNI (owner, 2026-10-13): the dataset's line shows alone too, whenever the dataset plays.
    const base = shown ? host.base?.() ?? null : null
    // The box is taller with any line in it: the first hint steps up over it.
    root?.classList.toggle('xr-phone-has-overlays', list.length > 0 || !!base)
    const baseRamp = overlayRows.querySelector<HTMLElement>(`button[data-action="palette"][data-overlay="${BASE_ROW}"] .xr-phone-overlay-ramp`)
    if (baseRamp && base) baseRamp.style.background = rampOf(base.colours)
    const key = list.map((o) => `${o.id}:${o.hidden ? 1 : 0}:${o.title}:${o.colours ? 1 : 0}`).join('|') + (base ? `|base:${base.hidden ? 1 : 0}:${base.title}:${base.colours?.scale ? 1 : 0}` : '')
    if (key === overlaysKey) return
    overlaysKey = key
    overlayRows.classList.toggle('hidden', list.length === 0 && !base)
    for (const off of rowDrags) off()
    rowDrags = []
    if (!list.length && !base) closePaletteDialog()
    rowIds = list.map((o) => o.id)
    overlayRows.textContent = ''
    for (const o of list) {
      const row = el('div', 'xr-phone-overlay' + (o.hidden ? ' is-hidden' : ''))
      // The grip and the palette button, left of the name, as on the browser's tracks.
      const stack = el('span', 'xr-phone-overlay-stack')
      const grip = iconButton('xr-phone-overlay-grip', 'grip', t('timeline.overlay.move', { title: o.title }))
      grip.dataset.overlay = o.id
      grip.dataset.action = 'grip'
      grip.disabled = list.length < 2
      const palette = el('button', 'xr-phone-overlay-palette', '<span class="xr-phone-overlay-ramp" aria-hidden="true"></span>')
      palette.type = 'button'
      palette.dataset.overlay = o.id
      palette.dataset.action = 'palette'
      palette.setAttribute('aria-haspopup', 'dialog')
      palette.setAttribute('aria-expanded', 'false')
      // AYNI (owner, 2026-10-12): hatched when the colours cannot change, but it opens, for the transparency.
      const paletteLabel = o.colours ? paletteButtonLabel(o.title) : t('palette.dialog.openFixed', { title: o.title })
      palette.setAttribute('aria-label', paletteLabel)
      palette.title = o.colours || !o.coloursReason ? paletteLabel : `${o.coloursReason}. ${paletteLabel}`
      palette.toggleAttribute('data-fixed', !o.colours)
      const ramp = palette.querySelector<HTMLElement>('.xr-phone-overlay-ramp')!
      if (o.colours) ramp.style.background = `linear-gradient(to top, ${o.colours.ramp.join(', ')})`
      stack.append(grip, palette)
      rowDrags.push(attachStackDrag(grip, {
        rows: () => Array.from(overlayRows.children).filter((r) => !r.classList.contains('xr-phone-overlay-base')) as HTMLElement[],
        row: () => row,
        onDrop: (from, to) => {
          const id = rowIds[from]
          if (id) host.moveOverlay(id, rowIds.length - 1 - to)
          update()
        },
      }))
      const name = el('span', 'xr-phone-overlay-name')
      name.textContent = o.title
      const eye = iconButton('xr-phone-tbtn xr-phone-overlay-btn', o.hidden ? 'eyeOff' : 'eye', t(o.hidden ? 'timeline.overlay.show' : 'timeline.overlay.hide', { title: o.title }))
      eye.dataset.overlay = o.id
      eye.dataset.action = 'eye'
      eye.setAttribute('aria-pressed', String(o.hidden))
      const close = iconButton('xr-phone-tbtn xr-phone-overlay-btn', 'close', t('timeline.overlay.close', { title: o.title }))
      close.dataset.overlay = o.id
      close.dataset.action = 'close'
      row.append(stack, name, eye, close)
      overlayRows.appendChild(row)
    }
    // AYNI (owner, 2026-10-12): the dataset's line at the foot, as the browser's green track:
    // a lock in place of the grip and palette (its colours and transparency are in View), its eye and its ✕.
    if (base) {
      const row = el('div', 'xr-phone-overlay xr-phone-overlay-base' + (base.hidden ? ' is-hidden' : ''))
      // The lock in the grip's place and the palette button, as on the browser's green track.
      const stack = el('span', 'xr-phone-overlay-stack')
      const lock = el('span', 'xr-phone-overlay-lock', svg('lockSmall'))
      lock.setAttribute('role', 'img')
      lock.setAttribute('aria-label', t('timeline.base.lock'))
      lock.title = t('timeline.base.lock')
      const palette = el('button', 'xr-phone-overlay-palette', '<span class="xr-phone-overlay-ramp" aria-hidden="true"></span>')
      palette.type = 'button'
      palette.dataset.overlay = BASE_ROW
      palette.dataset.action = 'palette'
      palette.setAttribute('aria-haspopup', 'dialog')
      palette.setAttribute('aria-expanded', 'false')
      const paletteLabel = base.colours?.scale ? paletteButtonLabel(base.title) : t('palette.dialog.openFixed', { title: base.title })
      palette.setAttribute('aria-label', paletteLabel)
      palette.title = base.colours?.scale || !base.coloursReason ? paletteLabel : `${base.coloursReason}. ${paletteLabel}`
      palette.toggleAttribute('data-fixed', !base.colours?.scale)
      palette.querySelector<HTMLElement>('.xr-phone-overlay-ramp')!.style.background = rampOf(base.colours)
      stack.append(lock, palette)
      const name = el('span', 'xr-phone-overlay-name')
      name.textContent = base.title
      const eye = iconButton('xr-phone-tbtn xr-phone-overlay-btn', base.hidden ? 'eyeOff' : 'eye', t(base.hidden ? 'timeline.base.show' : 'timeline.base.hide', { title: base.title }))
      eye.dataset.overlay = BASE_ROW
      eye.dataset.action = 'eye'
      eye.setAttribute('aria-pressed', String(base.hidden))
      const close = iconButton('xr-phone-tbtn xr-phone-overlay-btn', 'close', t('timeline.base.close', { title: base.title }))
      close.dataset.overlay = BASE_ROW
      close.dataset.action = 'close'
      row.append(stack, name, eye, close)
      overlayRows.appendChild(row)
    }
  }

  return {
    mount(parent) {
      if (root) return
      root = parent
      root.classList.add('xr-phone-on')
      for (const part of parts) {
        part.addEventListener('beforexrselect', stopXr)
        parent.appendChild(part)
      }
      update()
    },
    update,
    notice,
    sheetOpen: () => sheet !== null,
    openSheet,
    dispose() {
      for (const off of rowDrags) off()
      rowDrags = []
      closePaletteDialog()
      if (sheet === 'browse') host.closeBrowse()
      giveBackAll()
      returnPanels()
      for (const part of parts) {
        part.removeEventListener('beforexrselect', stopXr)
        part.remove()
      }
      if (noticeTimer !== null) window.clearTimeout(noticeTimer)
      root?.classList.remove('xr-phone-on', 'xr-sheet-open', 'xr-phone-placing', 'xr-phone-has-overlays', 'xr-phone-has-line')
      root = null
      sheet = null
    },
  }
}

function formatSpeed(speed: number): string {
  return Number.isInteger(speed) ? String(speed) : String(Math.round(speed * 100) / 100)
}
